import { AdminLayout } from "@/components/admin/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Users, MousePointer, UserCheck, TrendingUp } from "lucide-react";
import { format, subDays, startOfDay } from "date-fns";
import { ru } from "date-fns/locale";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { useMemo } from "react";

interface ReferralClick {
  id: string;
  referrer_telegram_id: number;
  clicked_at: string;
  converted: boolean;
  converted_user_id: string | null;
  converted_at: string | null;
  source: string | null;
}

interface TopReferrer {
  telegram_id: number;
  username: string | null;
  first_name: string | null;
  referral_count: number;
}

interface DailyStats {
  date: string;
  clicks: number;
  conversions: number;
}

export default function Referrals() {
  // Fetch referral clicks (last 30 days for chart)
  const { data: clicks, isLoading: clicksLoading } = useQuery({
    queryKey: ['referral-clicks'],
    queryFn: async () => {
      const thirtyDaysAgo = subDays(new Date(), 30).toISOString();
      const { data, error } = await supabase
        .from('referral_clicks')
        .select('*')
        .gte('clicked_at', thirtyDaysAgo)
        .order('clicked_at', { ascending: false });
      
      if (error) throw error;
      return data as ReferralClick[];
    },
  });

  // Fetch top referrers
  const { data: topReferrers } = useQuery({
    queryKey: ['top-referrers'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('telegram_users')
        .select('telegram_id, username, first_name, referral_count')
        .gt('referral_count', 0)
        .order('referral_count', { ascending: false })
        .limit(10);
      
      if (error) throw error;
      return data as TopReferrer[];
    },
  });

  // Calculate daily stats for chart
  const chartData = useMemo(() => {
    if (!clicks) return [];

    const dailyMap = new Map<string, { clicks: number; conversions: number }>();
    
    // Initialize last 14 days
    for (let i = 13; i >= 0; i--) {
      const date = format(subDays(new Date(), i), 'yyyy-MM-dd');
      dailyMap.set(date, { clicks: 0, conversions: 0 });
    }

    // Aggregate clicks by day
    clicks.forEach(click => {
      const date = format(new Date(click.clicked_at), 'yyyy-MM-dd');
      const existing = dailyMap.get(date);
      if (existing) {
        existing.clicks += 1;
        if (click.converted) {
          existing.conversions += 1;
        }
      }
    });

    // Convert to array
    return Array.from(dailyMap.entries()).map(([date, stats]) => ({
      date: format(new Date(date), 'dd MMM', { locale: ru }),
      clicks: stats.clicks,
      conversions: stats.conversions,
    }));
  }, [clicks]);

  // Calculate stats
  const totalClicks = clicks?.length || 0;
  const conversions = clicks?.filter(c => c.converted).length || 0;
  const conversionRate = totalClicks > 0 ? ((conversions / totalClicks) * 100).toFixed(1) : '0';
  const todayClicks = clicks?.filter(c => {
    const clickDate = new Date(c.clicked_at);
    const today = new Date();
    return clickDate.toDateString() === today.toDateString();
  }).length || 0;

  return (
    <AdminLayout title="Реферальная аналитика">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <MousePointer className="w-8 h-8 text-primary" />
              <div>
                <p className="text-sm text-muted-foreground">Всего кликов</p>
                <p className="text-2xl font-bold">{totalClicks}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <UserCheck className="w-8 h-8 text-green-500" />
              <div>
                <p className="text-sm text-muted-foreground">Конверсии</p>
                <p className="text-2xl font-bold">{conversions}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <TrendingUp className="w-8 h-8 text-blue-500" />
              <div>
                <p className="text-sm text-muted-foreground">Конверсия</p>
                <p className="text-2xl font-bold">{conversionRate}%</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8 text-yellow-500" />
              <div>
                <p className="text-sm text-muted-foreground">Сегодня</p>
                <p className="text-2xl font-bold">{todayClicks}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Chart */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>📈 Динамика за 14 дней</CardTitle>
        </CardHeader>
        <CardContent>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="colorClicks" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorConversions" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#22c55e" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis 
                  dataKey="date" 
                  className="text-xs fill-muted-foreground"
                  tick={{ fontSize: 12 }}
                />
                <YAxis 
                  className="text-xs fill-muted-foreground"
                  tick={{ fontSize: 12 }}
                  allowDecimals={false}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--card))', 
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px',
                  }}
                  labelStyle={{ color: 'hsl(var(--foreground))' }}
                />
                <Legend />
                <Area 
                  type="monotone" 
                  dataKey="clicks" 
                  name="Клики"
                  stroke="hsl(var(--primary))" 
                  fillOpacity={1} 
                  fill="url(#colorClicks)" 
                  strokeWidth={2}
                />
                <Area 
                  type="monotone" 
                  dataKey="conversions" 
                  name="Конверсии"
                  stroke="#22c55e" 
                  fillOpacity={1} 
                  fill="url(#colorConversions)" 
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-muted-foreground text-center py-8">Нет данных для графика</p>
          )}
        </CardContent>
      </Card>

      {/* Top Referrers */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>🏆 Топ рефереры</CardTitle>
        </CardHeader>
        <CardContent>
          {topReferrers && topReferrers.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>#</TableHead>
                  <TableHead>Пользователь</TableHead>
                  <TableHead>Telegram ID</TableHead>
                  <TableHead className="text-right">Приглашено</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {topReferrers.map((referrer, index) => (
                  <TableRow key={referrer.telegram_id}>
                    <TableCell className="font-medium">
                      {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : index + 1}
                    </TableCell>
                    <TableCell>
                      {referrer.first_name || referrer.username || 'Аноним'}
                      {referrer.username && (
                        <span className="text-muted-foreground ml-1">@{referrer.username}</span>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-sm">{referrer.telegram_id}</TableCell>
                    <TableCell className="text-right">
                      <Badge variant="secondary">{referrer.referral_count}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-muted-foreground text-center py-4">Нет данных о рефералах</p>
          )}
        </CardContent>
      </Card>

      {/* Recent Clicks */}
      <Card>
        <CardHeader>
          <CardTitle>Последние клики</CardTitle>
        </CardHeader>
        <CardContent>
          {clicksLoading ? (
            <p className="text-muted-foreground">Загрузка...</p>
          ) : clicks && clicks.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Дата</TableHead>
                  <TableHead>Реферер (Telegram ID)</TableHead>
                  <TableHead>Источник</TableHead>
                  <TableHead>Статус</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {clicks.map((click) => (
                  <TableRow key={click.id}>
                    <TableCell>
                      {format(new Date(click.clicked_at), 'dd MMM yyyy HH:mm', { locale: ru })}
                    </TableCell>
                    <TableCell className="font-mono">{click.referrer_telegram_id}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{click.source || 'telegram'}</Badge>
                    </TableCell>
                    <TableCell>
                      {click.converted ? (
                        <Badge className="bg-green-500">Конверсия ✓</Badge>
                      ) : (
                        <Badge variant="secondary">Клик</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-muted-foreground text-center py-4">Нет данных о кликах</p>
          )}
        </CardContent>
      </Card>
    </AdminLayout>
  );
}